import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface FieldLibraryItem {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  data_type: string;
  format: string | null;
  pattern: string | null;
  min_length: number | null;
  max_length: number | null;
  minimum: number | null;
  maximum: number | null;
  is_common: boolean;
  display_order: number | null;
  example_value: string | null;
}

export function useFieldLibraryDocs() {
  return useQuery({
    queryKey: ['field-library-docs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('field_library')
        .select('id, name, slug, description, data_type, format, pattern, min_length, max_length, minimum, maximum, is_common, display_order, example_value')
        .order('data_type', { ascending: true })
        .order('display_order', { ascending: true })
        .order('name', { ascending: true });
      
      if (error) throw error;
      
      // Group by data type
      const grouped = (data as FieldLibraryItem[]).reduce((acc, field) => {
        const type = field.data_type;
        if (!acc[type]) {
          acc[type] = [];
        }
        acc[type].push(field);
        return acc;
      }, {} as Record<string, FieldLibraryItem[]>);
      
      return {
        fields: data as FieldLibraryItem[],
        groupedByType: grouped,
      };
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - static reference data
  });
}

export function useFieldDoc(slug: string) {
  return useQuery({
    queryKey: ['field-doc', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('field_library')
        .select('id, name, slug, description, data_type, format, pattern, min_length, max_length, minimum, maximum, is_common, display_order, example_value')
        .eq('slug', slug)
        .single();

      if (error) throw error;
      return data as FieldLibraryItem;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - static reference data
    enabled: !!slug,
  });
}
